import { Home } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Search, Menu, X, ChevronDown, User, LogOut, Heart, LayoutDashboard, ShieldCheck, Briefcase, Building2, Bell, MapPin, ArrowRight, UserCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { useTheme } from "@/context/ThemeContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";


type NavLink = {
  label: string;
  href?: string;
  submenu?: Array<{ label: string; href: string }>;
};

const navLinks: NavLink[] = [
  { label: "Venues", href: "/venues" },
  { label: "Vendors", href: "/vendors" },
  { label: "Wedding Planners", href: "/wedding-planners" },
  { label: "Photographers", href: "/photographers" },
  { label: "About", href: "/about" },
];

const PORTAL_META: Record<string, { label: string; href: string; icon: React.ElementType; color: string }> = {
  admin:  { label: "Admin Portal",  href: "/portal/admin",  icon: ShieldCheck, color: "text-red-700" },
  vendor: { label: "Vendor Portal", href: "/portal/vendor", icon: Briefcase,   color: "text-[#D4AF37]" },
  venue:  { label: "Venue Portal",  href: "/portal/venue",  icon: Building2,   color: "text-purple-400" },
  user:   { label: "User Portal",   href: "/portal/profile", icon: UserCircle, color: "text-[#D4AF37]" },
};

function getInitials(name: string) {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

function getFirstName(name: string) {
  return name.split(" ")[0];
}

export function Navbar() {
  const { user, logout } = useAuth();

  useEffect(() => {
    console.log("[Navbar Auth State]", {
      userId: user?.id,
      name: user?.name,
      profileImage: user?.profileImage,
    });
  }, [user?.id, user?.name, user?.profileImage]);
  const { theme } = useTheme();
  const [, navigate] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<{
    cities: string[];
    vendors: { name: string; category: string; city: string }[];
    venues: { name: string; city: string; type: string }[];
  } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef    = useRef<HTMLDivElement>(null);
  const searchRef   = useRef<HTMLInputElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  const BASE_API = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") || "";

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSuggestions(null); return; }
    const id = setTimeout(async () => {
      try {
        const res = await fetch(`${BASE_API}/search?q=${encodeURIComponent(searchQuery)}`);
        if (res.ok) setSuggestions(await res.json() as typeof suggestions);
      } catch { setSuggestions(null); }
    }, 180);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchOpen(false);
    setSearchQuery("");
    navigate(`/vendors?search=${encodeURIComponent(q)}`);
  };

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchRef.current?.focus(), 80);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate("/");
  };

  const portalMeta = user ? PORTAL_META[user.role] : null;

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-[9999] flex flex-col shrink-0 bg-transparent">
        {/* Announcement Bar */}
        <div className="w-full bg-[#050403] py-2 flex items-center justify-between px-4 md:px-8 relative">
          <div className="flex-1 text-center">
            <div className="font-cinzel text-[10px] md:text-[12px] leading-none tracking-[0.15em] md:tracking-[0.3em] uppercase text-primary/85">
              ✦ India's Finest Event Planning Platform ✦
            </div>
          </div>

          <Link
            href="/"
            className="absolute right-4 md:right-8 lg:translate-x-[16px] flex items-center gap-1.5 font-cinzel text-[10px] md:text-[11px] tracking-[0.08em] uppercase text-primary/90 font-medium hover:text-primary transition-all duration-300"
          >
            <Home className="w-3.5 h-3.5" />
            <span>Home</span>
          </Link>
        </div>

        {/* Main Nav */}
          {/* BMS Logo - overlaps announcement bar and navbar only */}
  

  {/* BMS Logo - overlaps announcement bar and navbar */}

<div
          className={`w-full ${
            scrolled
              ? "bg-transparent backdrop-blur-0 border-b border-gray-200 shadow-none"
              : "bg-transparent backdrop-blur-0 border-b border-white/5"
          } px-4 md:px-8 relative flex items-center justify-center h-[58px] md:h-[56px] px-0 md:px-8 py-0 overflow-visible box-border transition-all duration-500 md:grid md:grid-cols-[240px_minmax(0,1fr)_auto] md:items-center`}
          
        >
          {/* Logo */}
          <div className="flex items-center gap-2 md:gap-3 h-full min-w-0 justify-self-start translate-y-[8px] md:translate-y-0 md:pl-0 lg:pl-0 lg:-translate-x-[8px]">
            <Link href="/" className="flex items-center cursor-pointer">
              <img
                src="/attached_assets/new_bmslogo.png"
                alt="BookMySquadd"
                className="h-9 sm:h-10 md:h-[42px] w-auto max-w-[150px] sm:max-w-[180px] md:max-w-[200px] object-contain object-center -translate-y-[6px] md:translate-y-0 translate-x-0 md:translate-x-0 lg:-translate-x-0"
              />
            </Link>
          </div>

          {/* Desktop Nav Links */}
<nav className="hidden md:flex h-full min-w-0 w-full items-center justify-center self-stretch gap-2 xl:gap-3 px-2 xl:px-4 overflow-hidden"> {navLinks.map((link) => (
              link.submenu ? (
                <DropdownMenu key={link.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="relative flex items-center gap-1 font-cinzel text-[11px] tracking-[0.15em] uppercase text-primary font-semibold hover:text-gray-800 transition-colors duration-300 whitespace-nowrap after:absolute after:bottom-0 after:left-0 after:h-px after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-300"
                    >
                      {link.label}
                      <ChevronDown className="w-3 h-3 text-primary/80" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="min-w-[220px] bg-[#0d0a07] border border-gray-200 shadow-2xl">
                    {link.submenu.map((item) => (
                      <DropdownMenuItem
                        key={item.href}
                        onSelect={() => navigate(item.href)}
                        className="px-4 py-3 text-gray-800/80 hover:text-gray-800 hover:bg-gray-50"
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.label}
                  href={link.href ?? "/"}
                  className="relative font-cinzel text-[11px] tracking-[0.15em] uppercase text-primary font-semibold hover:text-gray-800 transition-colors duration-300 whitespace-nowrap after:absolute after:bottom-0 after:left-0 after:h-px after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-300"
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center justify-end gap-2 sm:gap-3 md:gap-4 flex-shrink-0 min-w-0 justify-self-end">
            <button
              onClick={openSearch}
              className="hidden md:flex text-primary hover:text-gray-800 transition-colors p-1 font-semibold"
              aria-label="Search"
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Notification Bell */}
            {user && (
              <div ref={notifRef} className="relative">
                <button
                  onClick={() => setNotifOpen(o => !o)}
                  className="relative text-gray-700 hover:text-primary transition-colors p-1"
                  aria-label="Notifications"
                >
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-black font-bold rounded-full flex items-center justify-center" style={{ fontSize: "7px" }}>
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                <AnimatePresence>
                  {notifOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-3 w-80 bg-[#0d0a07] border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.7)] z-50"
                    >
                      <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between">
                        <p className="font-cinzel text-[9px] tracking-[0.22em] text-gray-700 uppercase">Notifications</p>
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className="font-cinzel text-[8px] tracking-[0.12em] text-primary/75 uppercase hover:text-primary transition-colors">Mark all read</button>
                        )}
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="font-manrope text-sm text-gray-700 text-center py-8">No notifications yet</p>
                        ) : (
                          notifications.map(n => (
                            <button key={n.id} onClick={() => { markRead(n.id); setNotifOpen(false); }}
                              className={`w-full flex items-start gap-3 px-4 py-3 border-b border-white/5 text-left hover:bg-white/4 transition-colors ${!n.read ? "bg-primary/4" : ""}`}>
                              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.read ? "bg-primary" : "bg-white/15"}`} />
                              <div className="flex-1 min-w-0">
                                <p className="font-cinzel text-[8.5px] tracking-[0.12em] uppercase text-gray-800/75 mb-0.5">{n.title}</p>
                                <p className="font-manrope text-xs text-gray-800/45 leading-snug">{n.message}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {user ? (
              /* Logged-in user avatar + dropdown */
              <div ref={userMenuRef} className="relative">
                <div className="flex items-center gap-2.5 group">
                  <Link
                    href="/portal/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="w-8 h-8 rounded-full bg-primary/20 border border-primary/50 flex items-center justify-center group-hover:border-primary transition-colors overflow-hidden shrink-0"
                    title="My Profile"
                  >
                    {user.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-cinzel text-[11px] text-primary font-bold">{getInitials(user.name)}</span>
                    )}
                  </Link>

                  <button
                    type="button"
                    onClick={() => setUserMenuOpen(o => !o)}
                    className="hidden md:flex items-center gap-1.5"
                    aria-label="User menu"
                  >
                    <span className="font-cinzel text-[16px] tracking-[0.1em] text-gray-800/80 max-w-[140px] truncate">
                      {getFirstName(user.name)}
                    </span>
                    <ChevronDown className={`w-3 h-3 text-gray-600 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-3 w-64 bg-[#0d0a07] border border-gray-200 shadow-2xl z-50"
                    >
                      {/* User info */}
                      <div className="px-4 py-2 border-b border-white/8">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center overflow-hidden">
                            {user.profileImage ? (
                              <img
                                src={user.profileImage}
                                alt={user.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-cinzel text-sm text-primary font-bold">{getInitials(user.name)}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-cinzel text-[11px] text-gray-800 font-semibold truncate">{user.name}</p>
                            <p className="font-manrope text-[10px] text-gray-600 truncate">{user.email}</p>
                            <span className="font-cinzel text-[14px] text-primary/70 uppercase tracking-wider">{user.role}</span>
                          </div>
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="py-1.5">
                        {/* Super Admin portal switcher */}
                        {user.id === 1 && (
                          <div className="px-4 py-2 border-b border-white/5">
                            <p className="font-cinzel text-[8px] tracking-[0.16em] text-primary/70 uppercase mb-1.5">
                              Switch Portal
                            </p>
                            <div className="space-y-0.5">
                              {Object.entries(PORTAL_META).map(([role, meta]) => {
                                const Icon = meta.icon;
                                return (
                                  <Link key={role} href={meta.href}>
                                    <button
                                      onClick={() => setUserMenuOpen(false)}
                                      className={`w-full flex items-center gap-3 px-2 py-2 font-manrope text-xs hover:bg-gray-50 transition-colors text-left ${meta.color}`}
                                    >
                                      <Icon className="w-3.5 h-3.5 opacity-70" />
                                      {meta.label}
                                    </button>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Role-specific portal link */}
                        {portalMeta && user.id !== 1 && (
                          <Link href={portalMeta.href}>
                            <button
                              onClick={() => setUserMenuOpen(false)}
                              className={`w-full flex items-center gap-3 px-4 py-3 font-manrope text-sm hover:bg-gray-50 transition-colors text-left border-b border-white/5 ${portalMeta.color}`}
                            >
                              <portalMeta.icon className="w-3.5 h-3.5 opacity-70" />
                              {portalMeta.label}
                            </button>
                          </Link>
                       )}

                        <Link href="/portal/saved">
                          <button
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-3 px-4 py-3 font-manrope text-sm text-gray-700 hover:text-gray-800 hover:bg-gray-50 transition-colors text-left"
                          >
                            <Heart className="w-3.5 h-3.5 text-primary/60" />
                            Saved Favourites
                          </button>
                        </Link>

                      </div>

                      <div className="border-t border-white/8 py-1.5">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 font-manrope text-sm text-red-700/80 hover:text-red-700 hover:bg-red-500/5 transition-colors text-left"
                        >
                          <LogOut className="w-3.5 h-3.5" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              /* Not logged in */
              <Link
                href="/login"
                className="relative hidden md:block font-cinzel text-[12px] tracking-[0.2em] uppercase text-primary font-semibold hover:text-gray-800 transition-colors pb-1 after:absolute after:bottom-0 after:left-0 after:h-px after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-300"
              >
                Login
              </Link>
            )}

            <Link
              href="/list-your-business"
              className="hidden md:flex items-center gap-1.5 font-cinzel text-[10px] tracking-[0.15em] uppercase border border-primary/70 text-primary px-3 py-2 hover:bg-primary/10 transition-colors group"
            >
              <span className="opacity-60 group-hover:opacity-100 transition-opacity text-[8px]">✦</span>
              List Business
            </Link>

            <button
              className="flex md:hidden items-center justify-center text-gray-800/80 hover:text-primary transition-colors p-2 shrink-0"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
          </div>
      </header>

      {/* Global Search Overlay */}
      <AnimatePresence>
        {searchOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[90] bg-transparent"
              onClick={() => setSearchOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              className="fixed top-[90px] left-1/2 -translate-x-1/2 z-[91] w-full max-w-2xl px-4"
            >
              <form onSubmit={handleSearchSubmit} className="relative">
                <div className="bg-[#0d0a07] border border-primary/30 shadow-[0_20px_60px_rgba(0,0,0,0.8),0_0_40px_rgba(212,175,55,0.1)] flex items-center gap-4 px-5 py-4">
                  <Search className="w-5 h-5 text-primary/60 shrink-0" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search vendors, services, cities…"
                    className="flex-1 bg-transparent border-none outline-none font-manrope text-base text-gray-800 placeholder:text-gray-800/25"
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery("")} className="text-gray-800/30 hover:text-gray-700 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="submit"
                    className="font-cinzel text-[9px] tracking-[0.2em] uppercase bg-primary text-black px-4 py-2 hover:bg-primary/85 transition-colors font-bold shrink-0"
                  >
                    Search
                  </button>
                </div>
                {suggestions && (suggestions.cities.length > 0 || suggestions.vendors.length > 0 || suggestions.venues.length > 0) && (
                  <div className="absolute top-full left-0 right-0 z-[100] bg-[#0d0a07] border border-primary/20 border-t-0 shadow-[0_20px_60px_rgba(0,0,0,0.9)] max-h-72 overflow-y-auto">
                    {suggestions.cities.length > 0 && (
                      <div className="p-2">
                        <p className="font-cinzel text-[8px] tracking-[0.25em] text-primary/40 uppercase px-3 py-1">Cities</p>
                        {suggestions.cities.map(city => (
                          <button key={city} type="button" onClick={() => { navigate(`/venues?city=${encodeURIComponent(city)}`); setSearchOpen(false); setSuggestions(null); setSearchQuery(""); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors text-left">
                            <MapPin className="w-3 h-3 text-primary/40 shrink-0" />
                            <span className="font-manrope text-sm text-gray-700">{city}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {suggestions.vendors.length > 0 && (
                      <div className={suggestions.cities.length > 0 ? "border-t border-white/5 p-2" : "p-2"}>
                        <p className="font-cinzel text-[8px] tracking-[0.25em] text-primary/40 uppercase px-3 py-1">Vendors</p>
                        {suggestions.vendors.map(v => (
                          <button key={v.name} type="button" onClick={() => { navigate(`/vendors?search=${encodeURIComponent(v.name)}`); setSearchOpen(false); setSuggestions(null); setSearchQuery(""); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors text-left">
                            <Briefcase className="w-3 h-3 text-primary/40 shrink-0" />
                            <div className="min-w-0">
                              <span className="font-manrope text-sm text-gray-700 block truncate">{v.name}</span>
                              <span className="font-cinzel text-[8px] tracking-[0.1em] text-primary/35 uppercase">{v.category} · {v.city}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {suggestions.venues.length > 0 && (
                      <div className={(suggestions.cities.length > 0 || suggestions.vendors.length > 0) ? "border-t border-white/5 p-2" : "p-2"}>
                        <p className="font-cinzel text-[8px] tracking-[0.25em] text-primary/40 uppercase px-3 py-1">Venues</p>
                        {suggestions.venues.map(v => (
                          <button key={v.name} type="button" onClick={() => { navigate(`/venues?search=${encodeURIComponent(v.name)}`); setSearchOpen(false); setSuggestions(null); setSearchQuery(""); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors text-left">
                            <Building2 className="w-3 h-3 text-primary/40 shrink-0" />
                            <div className="min-w-0">
                              <span className="font-manrope text-sm text-gray-700 block truncate">{v.name}</span>
                              <span className="font-cinzel text-[8px] tracking-[0.1em] text-primary/35 uppercase">{v.type} · {v.city}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-2 flex gap-2 flex-wrap">
                  {["Wedding Venues", "Photography", "Bridal Makeup", "Catering", "DJ & Music"].map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => { setSearchQuery(tag); searchRef.current?.focus(); }}
                      className="font-manrope text-[11px] text-gray-600 hover:text-primary/80 border border-gray-200 hover:border-primary/30 px-3 py-1 transition-all"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mob-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[10000] bg-white/60"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mob-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.32, 0, 0.67, 0], duration: 0.32 }}
            className="fixed top-0 right-0 z-[10001] h-screen w-[88vw] max-w-[360px] bg-[#0c0907] border-l border-gray-200 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-7 py-2 border-b border-white/8">
  <Link
  href="/"
  onClick={() => setMobileOpen(false)}
  className="flex items-center"
>
  <img
    src="/attached_assets/new_bmslogo.png"
    alt="BookMySquadd"
    className="h-9 w-auto max-w-[170px] object-contain"
  />
</Link>

  <button
                className="w-9 h-9 flex items-center justify-center border border-white/12 text-gray-600 hover:text-primary hover:border-primary/40 transition-colors"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* User chip */}
            {user && (
              <div className="px-7 py-4 border-b border-white/6 flex items-center gap-3 bg-primary/4">
                <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
                  <span className="font-cinzel text-[11px] text-primary font-bold">{getInitials(user.name)}</span>
                </div>
                <div className="min-w-0">
                  <p className="font-cinzel text-[11px] text-gray-800 truncate">{user.name}</p>
                  <p className="font-cinzel text-[8px] text-primary/60 uppercase tracking-wider">{user.role}</p>
                </div>
              </div>
            )}

            {/* Nav links */}
            <nav className="flex-1 overflow-y-auto px-5 sm:px-7 py-6 sm:py-8 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href ?? "/"}
                  className="font-cinzel text-[13px] tracking-[0.22em] uppercase text-gray-800/65 hover:text-primary transition-colors py-3.5 border-b border-white/5 flex items-center justify-between group"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-primary" />
                </Link>
              ))}

              <div className="pt-6 mt-2 flex flex-col gap-1">
                {user ? (
                  <>
                    {portalMeta && (
                      <Link href={portalMeta.href} onClick={() => setMobileOpen(false)}
                        className={`font-cinzel text-[12px] tracking-[0.18em] uppercase py-3 flex items-center gap-2.5 border-b border-white/5 ${portalMeta.color}`}>
                        <portalMeta.icon className="w-3.5 h-3.5 opacity-70 shrink-0" />
                        {portalMeta.label}
                      </Link>
                    )}
                   
                    <button
                      onClick={async () => { setMobileOpen(false); await handleLogout(); }}
                      className="font-cinzel text-[12px] tracking-[0.18em] uppercase text-red-700/60 hover:text-red-700 transition-colors py-3 text-left flex items-center gap-2.5"
                    >
                      <LogOut className="w-3.5 h-3.5 opacity-60 shrink-0" />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link href="/login" onClick={() => setMobileOpen(false)}
                    className="font-cinzel text-[12px] tracking-[0.18em] uppercase text-primary font-semibold hover:text-gray-800 transition-colors py-3 border-b border-white/5">
                    Login / Register
                  </Link>
                )}

                <Link href="/list-your-business" onClick={() => setMobileOpen(false)}
                  className="font-cinzel text-[11px] tracking-[0.08em] uppercase text-primary/70 hover:text-primary transition-colors pt-4 flex items-center gap-1.5">
                  <span className="text-[8px]">✦</span> List Business
                </Link>
              </div>
            </nav>

          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}
